import { Icon, type IconDefinition } from '@vrplatform/icons';
import type { Maybe } from '@vrplatform/utils';
import { Box, Group, Modal, Text, VisuallyHidden, rem } from '@mantine/core';
import { type ComponentProps, useCallback, useState } from 'react';
import { Button } from '../button';

type Props =
  | {
      secondConfirm: {
        title: string;
        subtitle: string;
        submitTitle?: string;
      };
      onSubmit: (
        isSecondConfirm: boolean
        // biome-ignore lint/suspicious/noConfusingVoidType: <explanation>
      ) => Promise<{ showConfirm: boolean } | void>;
    }
  | {
      secondConfirm?: undefined;
      onSubmit: () => void;
    };

type FirstProps = {
  opened: boolean;
  closeModal: () => void;
  loading: boolean;
  submitTitle?: string;
  submitIcon?: IconDefinition;
  title: string;
  subtitle: string;
  entityTitle?: Maybe<string>;
  type: 'confirm' | 'delete' | 'warning';
  icon?: IconDefinition;
  size?: ComponentProps<typeof Modal>['size'];
  zIndex?: ComponentProps<typeof Modal>['zIndex'];
  withinPortal?: ComponentProps<typeof Modal>['withinPortal'];
} & Props;

export const ConfirmModal = (props: FirstProps) => {
  const [confirmOpened, setConfirmOpened] = useState<boolean>(false);

  const closeSecond = useCallback(() => {
    setConfirmOpened(false);
    props.closeModal();
  }, [props.closeModal]);

  const openSecond = useCallback(() => {
    setConfirmOpened(true);
  }, []);

  return (
    <>
      <First
        {...props}
        opened={props.opened && !confirmOpened}
        openConfirm={openSecond}
      />
      {props.secondConfirm && (
        <Second
          {...props}
          onSubmit={props.onSubmit}
          opened={confirmOpened}
          closeModal={closeSecond}
          title={props.secondConfirm.title}
          subtitle={props.secondConfirm.subtitle}
          submitTitle={props.secondConfirm.submitTitle}
        />
      )}
    </>
  );
};

const First = ({
  closeModal,
  opened,
  loading,
  onSubmit,
  submitTitle,
  title,
  entityTitle,
  subtitle,
  type,
  icon,
  submitIcon,
  size = 380,
  zIndex,
  secondConfirm,
  openConfirm,
  withinPortal,
}: FirstProps & {
  openConfirm: () => void;
}) => {
  const isSecondConfirm = !!secondConfirm;

  const submit = useCallback(async () => {
    if (isSecondConfirm) {
      await onSubmit(false).then((result) => {
        if (result?.showConfirm) {
          openConfirm();
        }
      });
    } else {
      onSubmit();
    }
  }, [isSecondConfirm, onSubmit, openConfirm]);

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      closeButtonProps={{ 'aria-label': 'Close dialog' }}
      title={<VisuallyHidden>{title}</VisuallyHidden>}
      size={size}
      radius="md"
      centered
      withinPortal={withinPortal}
      zIndex={zIndex}
      styles={{
        body: {
          paddingInline: rem(30),
          paddingBottom: rem(30),
        },
        header: {
          paddingBottom: 0,
          minHeight: 0,
        },
      }}
    >
      <Box
        mb={'xl'}
        sx={() => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        })}
      >
        <Icon
          icon={icon || (type === 'confirm' ? 'CheckCircleIcon' : 'TrashIcon')}
          color={({ colors, primaryColor }) =>
            colors[
              type === 'confirm'
                ? primaryColor
                : type === 'delete'
                  ? 'red'
                  : 'orange'
            ][6]
          }
          size={30}
        />
        <Text fw={500} component="h3" ta="center" size="lg" mt="xl" mb="md">
          {title}{' '}
          {entityTitle && (
            <>
              <br />
              {entityTitle}
            </>
          )}
        </Text>

        <Text component="p" c="neutral" ta="center" size="sm">
          {subtitle}
        </Text>
      </Box>

      <Group wrap="nowrap">
        <Button
          sx={{
            width: 100,
          }}
          onClick={closeModal}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={submit}
          leftIcon={submitIcon}
          variant={type === 'confirm' ? 'primary' : 'filled'}
          loading={loading}
          color={
            type === 'confirm'
              ? undefined
              : type === 'delete'
                ? 'red'
                : 'orange'
          }
          sx={{
            flex: 1,
            fontWeight: type === 'confirm' ? undefined : 500,
          }}
        >
          {submitTitle || (type === 'confirm' ? 'Yes, submit' : 'Yes, delete')}
        </Button>
      </Group>
    </Modal>
  );
};

const Second = ({
  closeModal,
  opened,
  loading,
  onSubmit,
  submitTitle,
  subtitle,
  title,
}: {
  opened: boolean;
  closeModal: () => void;
  loading: boolean;
  onSubmit: FirstProps['onSubmit'];
  title: string;
  subtitle: string;
  submitTitle: string | undefined;
}) => {
  return (
    <Modal
      radius="md"
      opened={opened}
      onClose={closeModal}
      closeButtonProps={{ 'aria-label': 'Close dialog' }}
      title={<VisuallyHidden>{title}</VisuallyHidden>}
      centered
      size={500}
      styles={(theme) => ({
        body: {
          paddingBottom: theme.spacing.xl,
          paddingInline: theme.spacing.xl,
        },
      })}
    >
      <Icon
        icon="AlertTriangleIcon"
        color={(theme) => theme.colors.orange[5]}
        size={32}
      />
      <Text ta="center" component="h4" size="lg" fw={500} my="lg">
        {title}
      </Text>
      <Text ta="center" component="p" c="neutral" mb="xl">
        {subtitle}
      </Text>

      <Group wrap="nowrap">
        <Button type="button" onClick={closeModal} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          sx={{ flex: 1 }}
          onClick={async () => {
            await onSubmit(true);
            closeModal();
          }}
          loading={loading}
        >
          {submitTitle || 'Yes, submit'}
        </Button>
      </Group>
    </Modal>
  );
};
